import React, {
  useState,
  useEffect,
  useMemo,
  Suspense,
  lazy,
  useCallback,
} from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes from "routes";
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { debounce } from "lodash";
import StartApplicationForm from "views/pages/student_dashboard/StartApplicationForm";
import ImpersonationBanner from "components/ImpersonationBanner";
// import Brightness6Icon from '@mui/icons-material/Brightness6';

// Lazy load components
const Login = lazy(() => import("views/pages/login/Login"));
const OrganizationRegister = lazy(() =>
  import("views/pages/register/organization_register/OrganizationRegister")
);
const StudentLogin = lazy(() =>
  import("views/pages/login/student_login/StudentLogin")
);
const StudentRegister = lazy(() => import("views/pages/register/Register"));
const ForgotPassword = lazy(() =>
  import("views/pages/forgot_password/ForgotPassword")
);
const StudentDashboard = lazy(() =>
  import("views/pages/student_dashboard/StudentDashboardV3")
);
const Degrees = lazy(() =>
  import("views/pages/student_dashboard/Pages/Degrees")
);
const MyApplications = lazy(() =>
  import("views/pages/student_dashboard/Pages/MyApplications")
);
const ViewApplicationSteps = lazy(() =>
  import("views/pages/student_dashboard/Pages/ViewApplicationSteps")
);
const ApplicationFormV3 = lazy(() =>
  import("views/pages/student_dashboard/Pages/ApplicationFormV3")
);
const ResubmitForm = lazy(() =>
  import("views/pages/student_dashboard/Pages/ResubmitForm")
);
const LandingPage = lazy(() => import("views/pages/landing_page/LandingPage"));
const ApplicationForm = lazy(() =>
  import("views/pages/application_form/ApplicationForm")
);
const ApplicationFormTwo = lazy(() =>
  import("views/pages/application_form_two/ApplicationFormTwo")
);
const FormSteps = lazy(() => import("views/pages/forms/FormSteps"));
const StepFields = lazy(() => import("views/pages/forms/StepField"));
const UploadMobile = lazy(() =>
  import("views/pages/upload_mobile/UploadMobile")
);
const ViewApplication = lazy(() =>
  import("views/pages/view_application/ViewApplication")
);
const BZUMeritLists = lazy(() =>
  import("views/pages/public_MeritList/bzuMeritLists")
);
const TestSlip = lazy(() => import("views/pages/test_slip/TestSlip"));
const PreviewVoucher = lazy(() =>
  import("views/pages/fee_management/PreviewVoucher")
);
const ViewApplicationTwo = lazy(() =>
  import("views/pages/view_application/ViewApplicationTwo")
);
const ApplyDegree = lazy(() => import("views/pages/degree/ApplyDegree"));
const DirectLogin = lazy(() => import("views/pages/login/DirectLogin"));
const NotFound = lazy(() => import("views/pages/404/NotFound"));
const BZUApplicationForm = lazy(() =>
  import("views/pages/student_dashboard/Pages/BZUApplicationForm")
);
// const BZUApplicationFormAdmin = lazy(() =>
//   import("views/pages/student_dashboard/Pages/BZUApplicationFormAdmin")
// );
// const Voucher = lazy(() =>
//   import("views/pages/student_dashboard/Pages/Voucher")
// );
const VoucherTwo = lazy(() =>
  import("views/pages/student_dashboard/Pages/Voucher2")
);
const Letter = lazy(() =>
  import("views/pages/student_dashboard/Pages/AdmissionLetter")
);
const GetApplications = lazy(() =>
  import("views/pages/Applications/ViewApplication")
);
const ViewAdmissionForm = lazy(() =>
  import("views/pages/Applications/ViewAdmissionForm")
);
const ModifiedApplications = lazy(() =>
  import("views/pages/student_dashboard/Pages/ModifiedApplications")
);
const LoaderPage = lazy(() =>
  import("views/pages/student_dashboard/Components/LoaderPage")
);
const AssignPrograms = lazy(() =>
  import("views/pages/campaign/AssignPrograms")
);
const AllProgramsApplicationsReport = lazy(() =>
  import("views/pages/Reports/AllProgramsApplicationsReport")
);
// App Component
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Create RTL cache
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Handle mouse enter/leave
  const debouncedSetMiniSidenav = useCallback(
    debounce((value) => setMiniSidenav(dispatch, value), 300),
    [dispatch]
  );

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      debouncedSetMiniSidenav(false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      debouncedSetMiniSidenav(true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.type === "nested") {
        return getRoutes(route.children);
      }
      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={
              <Suspense fallback={<div>Loading...</div>}>
                {route.component}
              </Suspense>
            }
            key={route.key}
          />
        );
      }
      return <Route path="/*" element={<NotFound />} key="404" />;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      right="2rem"
      zIndex={99}
      color="dark"
      onClick={handleConfiguratorOpen}
    >
      {/* <Brightness6Icon fontSize="small" color="inherit" >
        settings
      </Brightness6Icon> */}
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName="Admission System"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route path="/admin" element={<Login />} />
            <Route path="/register" element={<OrganizationRegister />} />
            {getRoutes(routes)}
            <Route path="/*" element={<NotFound />} />
            <Route
              path="/"
              element={<StudentLogin />}
            />
            <Route
              path="/student-register"
              element={<StudentRegister />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/student-dashboard"
              element={<StudentDashboard />}
            />
            <Route
              path="/student-dashboard-degrees/:organization_id"
              element={<Degrees />}
            />
            <Route
              path="/student-dashboard-applications"
              element={<MyApplications />}
            />
            <Route
              path="/bzu-application-form"
              element={<BZUApplicationForm />}
            />
            {/* <Route
              path="/bzu-application-form-admin"
              element={<BZUApplicationFormAdmin />}
            /> */}
            <Route
              path="/student-dashboard-view-application/:organization_id"
              element={<ViewApplicationSteps />}
            />
            <Route
              path="/student-application-form/:organization_id"
              element={<ApplicationFormV3 />}
            />
            <Route
              path="/student-application-resubmit-form/:organization_id"
              element={<ResubmitForm />}
            />
            <Route path="/landing-page" element={<LandingPage />} />
            <Route path="/form-steps/:form_id" element={<FormSteps />} />
            <Route
              path="/form/:form_id/step/:step_id"
              element={<StepFields />}
            />
            <Route
              path="/application_form_temp/:application_id"
              element={<ApplicationForm />}
            />
            <Route
              path="/application_form/:application_id/:form_id"
              element={<ApplicationFormTwo />}
            />
            <Route
              path="/upload-from-mobile/:application_id/:user_id/:type"
              element={<UploadMobile />}
            />
            <Route
              path="/view-application/:application_id"
              element={<ViewApplication />}
            />
            <Route
              path="/view-application-two/:application_id"
              element={<ViewApplicationTwo />}
            />
            <Route path="/test-slip" element={<TestSlip />} />
            <Route path="/loader" element={<LoaderPage />} />
            <Route
              path="/apply-degree/:degree_id/:organization_id"
              element={<ApplyDegree />}
            />
            <Route path="/direct-login" element={<DirectLogin />} />
            <Route path="/assign-programs/:campaignId" element={<AssignPrograms />} />
            <Route
              path="/all_programs_applications_report"
              element={<AllProgramsApplicationsReport />}
            />
          </Routes>
        </Suspense>
        <ImpersonationBanner />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName="Admission System"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/admin" element={<Login />} />
          <Route path="/register" element={<OrganizationRegister />} />
          {getRoutes(routes)}
          <Route path="/*" element={<NotFound />} />
          <Route
            path="/"
            element={<StudentLogin />}
          />
          <Route
            path="/student-register"
            element={<StudentRegister />}
          />
          <Route
            path="/forgot-password"
            element={<ForgotPassword />}
          />
          <Route
            path="/student-dashboard"
            element={<StudentDashboard />}
          />
          <Route
            path="/student-dashboard-degrees/:organization_id"
            element={<Degrees />}
          />
          <Route
            path="/student-dashboard-applications"
            element={<MyApplications />}
          />
          <Route
              path="/bzu-application-form"
              element={<BZUApplicationForm />}
            />
            {/* <Route
              path="/bzu-application-form-admin"
              element={<BZUApplicationFormAdmin />}
            /> */}
          <Route
            path="/student-dashboard-view-application/:organization_id"
            element={<ViewApplicationSteps />}
          />
          <Route
            path="/student-application-form/:organization_id"
            element={<ApplicationFormV3 />}
          />
          <Route
            path="/student-application-resubmit-form/:organization_id"
            element={<ResubmitForm />}
          />
          <Route path="/landing-page" element={<LandingPage />} />
          <Route path="/form-steps/:form_id" element={<FormSteps />} />
          <Route path="/form/:form_id/step/:step_id" element={<StepFields />} />
          <Route
            path="/application_form_temp/:application_id"
            element={<ApplicationForm />}
          />
          <Route
            path="/application_form/:application_id/:form_id"
            element={<ApplicationFormTwo />}
          />
          <Route
            path="/upload-from-mobile/:application_id/:user_id/:type"
            element={<UploadMobile />}
          />
          <Route
            path="/view-application/:application_id"
            element={<ViewApplication />}
          />
          <Route
            path="/view-application-two/:application_id"
            element={<ViewApplicationTwo />}
          />
          <Route path="/test-slip" element={<TestSlip />} />
          <Route path="/loader" element={<LoaderPage />} />
          <Route path="/preview-voucher" element={<PreviewVoucher />} />
          <Route
            path="/apply-degree/:degree_id/:organization_id"
            element={<ApplyDegree />}
          />
          <Route path="/direct-login" element={<DirectLogin />} />
          <Route path="/start-application-form/:degree/:postIntermediateDegree?" element={<StartApplicationForm />} />
          {/* <Route path="/voucher" element={<Voucher/>}/> */}
          <Route path="/get-applications/:id" element={<GetApplications/>}/>
          <Route path="/view-admission-form/:id" element={<ViewAdmissionForm/>}/>
          <Route path="/fee-voucher/:voucher_id" element={<VoucherTwo/>}/>
          <Route path="/admission-letter/:selected_id" element={<Letter/>}/>
          <Route path="/bzu_public_merit_lists" element={<BZUMeritLists/>}/>
          <Route path="/modified-application/:id" element={<ModifiedApplications/>}/>
          <Route path="/assign-programs/:campaignId" element={<AssignPrograms />} />
          <Route
            path="/all_programs_applications_report"
            element={<AllProgramsApplicationsReport />}
          />
        </Routes>
      </Suspense>
      <ImpersonationBanner />
    </ThemeProvider>
  );
}
